<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderTabs.vue.
  // Persisted tab meta resolves from ordersStore (socket-fed) instead of the
  // -pos ops read model.
  import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
  import { useNow } from '@vueuse/core';
  import { OrderType } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IPosDraft } from '@/domain/pos/pos-types';
  import {
    applyTabDisambiguation,
    getOrderTabDisplayModel,
    IOrderTabDisambiguationModel,
  } from '@/domain/pos/order-formatting';
  import { getPosOrderTypeLabel } from '@/domain/pos/order-type-ui';
  import { resolveAllowedOrderType } from '@/domain/pos/order-type-policies';
  import { useModal } from '@/composition/use-modal';
  import { usePosMainMode } from '@/composition/use-pos-main-mode';
  import { useUi } from '@/composition/use-ui';
  import { usePosDraftsStore } from '@/stores/pos-drafts.store';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';

  const posDrafts = usePosDraftsStore();
  const ordersStore = useOrdersStore();
  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();
  const { mainMode } = usePosMainMode();
  const now = useNow({ interval: 30_000 });

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Actions

  function handleAddDraft() {
    const branch = restaurantStore.selectedBranch;
    if (branch?.allowOrders === false) {
      ui.showToast(AlertType.Info, i18nT('posV2.branchNotAcceptingOrders'));
      return;
    }

    const flags = {
      tableOrders: branch?.tableOrders ?? null,
      takeAway: branch?.takeAway ?? null,
      delivery: branch?.delivery ?? null,
    };

    // Preferred type follows the main page's selected mode (-pos parity):
    // "+" from an Away session creates an Away draft, not a copy of the
    // current tab's type.
    const preferred = mainMode.value;
    const type = resolveAllowedOrderType(flags, preferred);
    if (!type) {
      ui.showToast(AlertType.Info, i18nT('posV2.flowDisabled'));
      return;
    }
    if (type !== preferred) {
      ui.showToast(
        AlertType.Info,
        i18nT('posV2.typeFallbackToast', {
          preferred: getPosOrderTypeLabel(preferred),
          type: getPosOrderTypeLabel(type),
        })
      );
    }

    posDrafts.createDraft(type);
  }

  function handleSelectTab(id: string) {
    posDrafts.setActiveDraft(id);
  }

  async function handleCloseTab(id: string, event: Event) {
    event.stopPropagation();

    const draft = posDrafts.drafts.find((d) => d.id === id);
    if (!draft) return;

    if (draft.isDirty) {
      const confirmed = await modals.confirm({
        title: i18nT('posV2.closeOrderTitle'),
        message: i18nT('posV2.closeOrderConfirm'),
        acceptText: i18nT('posV2.close'),
        acceptColor: 'warning',
      });
      if (!confirmed) return;
    }

    posDrafts.closeDraft(id, true);
  }

  //#endregion

  //#region Tab display models

  function resolveTableName(tableId?: number | null): string | null {
    const id = Number(tableId ?? 0);
    if (!Number.isFinite(id) || id <= 0) return null;
    const name = String(
      restaurantStore.tables?.find((t) => t.id === id)?.name ?? ''
    ).trim();
    return name || null;
  }

  const tabModels = computed(() => {
    const models: IOrderTabDisambiguationModel[] = [];

    for (const draft of posDrafts.drafts) {
      let type: OrderType = draft.type ?? OrderType.Seat;
      let humanId: string | null = null;
      let serverId: number | null = draft.orderId ?? null;
      let customerName: string | null = draft.customerData?.name || null;
      let customerPhone: string | null = draft.customerData?.phone || null;
      let deliveryAddress: string | null = draft.deliveryInfo?.address ?? null;
      let deliveryAddress2: string | null =
        draft.deliveryInfo?.address2 ?? null;
      let tableName: string | null = resolveTableName(draft.tableId ?? null);

      let isPersisted = false;
      let timeMs: number | null = null;

      if (draft.orderId) {
        const order = ordersStore.orders?.find((o) => o.id === draft.orderId);
        if (order) {
          isPersisted = true;
          type = order.type ?? type;
          humanId = order.humanId ?? null;
          serverId = order.id;
          customerName = order.userName ?? customerName;
          customerPhone = order.userTel ?? customerPhone;
          deliveryAddress = order.userAddress ?? deliveryAddress;
          deliveryAddress2 = order.userAddress2 ?? deliveryAddress2;
          tableName = resolveTableName(order.tableId) ?? tableName;

          const t = new Date(order.updatedAt ?? order.createdAt).getTime();
          timeMs = Number.isFinite(t) && t > 0 ? t : null;
        }
      }

      const display = getOrderTabDisplayModel({
        type,
        humanId,
        serverId,
        localId: null,
        customerName,
        customerPhone,
        tableName,
        deliveryAddress,
        deliveryAddress2,
        preferAwayDraftFallback: type === OrderType.Away && !draft.orderId,
      });

      models.push({
        key: draft.id,
        ...display,
        isPersisted,
        timeMs,
      });
    }

    return applyTabDisambiguation(models);
  });

  /** Only create-mode drafts can get old (order-bound tabs are auto-pruned). */
  const STALE_DRAFT_AGE_MS = 4 * 60 * 60 * 1000;

  function getDraftAgeLabel(draft: IPosDraft): string | null {
    if (draft.mode !== 'create') return null;
    const age = now.value.getTime() - draft.lastTouchedAt;
    if (!Number.isFinite(age) || age < STALE_DRAFT_AGE_MS) return null;
    const hours = Math.floor(age / 3_600_000);
    if (hours < 48) return i18nT('posV2.tabAgeHours', { n: hours });
    return i18nT('posV2.tabAgeDays', { n: Math.floor(hours / 24) });
  }

  const tabViewModels = computed(() => {
    const byKey = new Map(tabModels.value.map((m) => [m.key, m]));
    return posDrafts.drafts.map((draft) => ({
      draft,
      display: byKey.get(draft.id) ?? null,
      // Units, not rows: a line with quantity 2 counts as 2.
      itemCount: draft.cartLines.reduce((sum, l) => sum + l.quantity, 0),
      ageLabel: getDraftAgeLabel(draft),
    }));
  });

  //#endregion

  //#region Auto-scroll to active tab

  const rootEl = ref<HTMLElement>();

  watch(
    () => posDrafts.activeDraftId,
    async () => {
      await nextTick();
      const scrollEl =
        rootEl.value?.querySelector<HTMLElement>('.tabs-scroll') ?? null;
      if (!scrollEl) return;

      const activeTab = scrollEl.querySelector('[data-active="true"]');
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    },
    { flush: 'post' }
  );

  //#endregion
</script>

<template>
  <div ref="rootEl" class="pos-v2-order-tabs">
    <PosUiScrollable
      orientation="horizontal"
      variant="pseudo"
      theme="default"
      :offset-left="10"
      :offset-right="10"
      size="md"
      :arrows="true"
      scroll-class="tabs-scroll"
    >
      <div
        v-for="t in tabViewModels"
        :key="t.draft.id"
        role="button"
        tabindex="0"
        class="tab"
        :class="{ active: t.draft.id === posDrafts.activeDraftId }"
        :data-active="t.draft.id === posDrafts.activeDraftId"
        @click="handleSelectTab(t.draft.id)"
        @keydown.enter.prevent="handleSelectTab(t.draft.id)"
      >
        <div class="tab-content">
          <PosUiIcon
            :name="t.display?.titleIcon ?? 'receipt'"
            class="tab-leading-icon"
          />

          <div class="tab-lines">
            <span
              v-if="
                t.display &&
                t.display.kindLabel.trim().toLowerCase() !==
                  t.display.titleText.trim().toLowerCase()
              "
              class="tab-kind"
            >
              {{ t.display.kindLabel }}
            </span>
            <span v-else-if="!t.display" class="tab-kind">
              {{ $t('posV2.order') }}
            </span>

            <div
              class="tab-title-row"
              :title="
                (t.display?.titleText ?? '') +
                (t.display?.titleSuffix ? ` ${t.display.titleSuffix}` : '')
              "
            >
              <span class="tab-title-text">
                {{ t.display?.titleText ?? $t('posV2.new') }}
              </span>
              <span v-if="t.display?.titleSuffix" class="tab-title-suffix">
                {{ t.display.titleSuffix }}
              </span>
            </div>
          </div>

          <PosUiBadge
            v-if="t.itemCount > 0"
            :label="t.itemCount"
            color="primary"
            variant="solid"
            size="xs"
            class="tab-badge"
          />

          <PosUiBadge
            v-if="t.ageLabel"
            :label="t.ageLabel"
            color="warning"
            variant="soft"
            size="xs"
            class="tab-age"
          />
        </div>

        <span
          class="tab-close"
          role="button"
          tabindex="0"
          :aria-label="$t('posV2.closeTab')"
          @click="(e) => handleCloseTab(t.draft.id, e)"
          @keydown.enter.stop.prevent="(e) => handleCloseTab(t.draft.id, e)"
        >
          <PosUiIcon name="close" />
        </span>
      </div>

      <button type="button" class="tab tab-new" @click="handleAddDraft">
        <PosUiIcon name="plus" class="tab-new-icon" />
        <span class="tab-new-label">{{ $t('posV2.new') }}</span>
      </button>
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  // Browser-tab strip on a neutral track; the active tab is white with an
  // orange top accent (brand marks position, not fills).
  .pos-v2-order-tabs {
    @apply tw-relative tw-border-b tw-border-neutral-200 tw-bg-neutral-100;

    :deep(.tabs-scroll) {
      @apply tw-flex tw-gap-0.5 tw-overflow-x-auto tw-px-1.5 tw-pt-1;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        @apply tw-hidden;
      }
    }

    .tab {
      @apply tw-relative tw-flex tw-h-[46px] tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-gap-2 tw-overflow-hidden tw-rounded-t-lg tw-border tw-border-b-0 tw-border-transparent tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-neutral-600 tw-transition-colors tw-duration-150 hover:tw-bg-neutral-200/70;

      &::before {
        // Active top accent (kept in layout so tabs don't shift).
        content: '';
        @apply tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-[3px] tw-bg-transparent;
      }

      &.active {
        @apply tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;

        &::before {
          @apply tw-bg-orange-600;
        }
      }

      &:not(.active):hover .tab-close {
        @apply tw-opacity-100;
      }

      .tab-content {
        @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2;
      }

      .tab-leading-icon {
        @apply tw-flex-shrink-0 tw-text-base tw-text-neutral-500;
      }

      .tab-lines {
        @apply tw-flex tw-min-w-0 tw-flex-col;
      }

      .tab-kind {
        @apply tw-text-[10px] tw-leading-none tw-text-neutral-500;
      }

      .tab-title-row {
        @apply tw-flex tw-min-w-0 tw-items-center tw-gap-1;
      }

      .tab-title-text {
        @apply tw-min-w-0 tw-max-w-[160px] tw-truncate tw-text-sm tw-font-semibold tw-leading-tight tw-text-neutral-900;
      }

      .tab-title-suffix {
        @apply tw-flex-shrink-0 tw-text-xs tw-text-neutral-500;
      }

      .tab-badge {
        @apply tw-flex-shrink-0;
      }

      .tab-age {
        @apply tw-flex-shrink-0;
      }

      .tab-close {
        @apply tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-self-center tw-rounded tw-text-neutral-500 tw-opacity-0 tw-transition-opacity hover:tw-bg-neutral-200 hover:tw-text-neutral-700;
      }

      &.active .tab-close {
        @apply tw-opacity-100;
      }
    }

    .tab-new {
      @apply tw-flex-col tw-justify-center tw-gap-0.5 tw-border tw-border-b-0 tw-border-dashed tw-border-neutral-300 tw-text-neutral-500;

      &:hover {
        @apply tw-bg-neutral-200/60 tw-text-neutral-700;
      }

      .tab-new-icon {
        @apply tw-text-sm;
      }

      .tab-new-label {
        @apply tw-text-xs;
      }
    }
  }
</style>
